import NumberInput from '../../../components/NumberInput'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { supabase, supabaseConfigured } from '../../../lib/supabaseClient'

// Pre-fills body weight from the user's most recent Phoenix weight_entries
// row. Failures are swallowed — pre-fill is a nicety, not a requirement.
function usePrefillBodyWeight(userId, currentValue, onChange) {
  const tried = useRef(false)
  useEffect(() => {
    if (tried.current) return undefined
    if (currentValue) { tried.current = true; return undefined }
    if (!supabaseConfigured || !userId) return undefined
    tried.current = true
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('weight_entries')
        .select('weight')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (!cancelled && data?.weight) {
        onChange({ body_weight_kg: String(data.weight) })
      }
    })()
    return () => { cancelled = true }
  }, [userId, currentValue, onChange])
}

export default function SessionMetaForm({ meta, onChange, userId }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  usePrefillBodyWeight(userId, meta.body_weight_kg, onChange)

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-between w-full"
          aria-expanded={open}
        >
          <span className="text-sm font-semibold text-foreground">
            {t('workout.meta.toggle')}
          </span>
          {open
            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {open && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('workout.meta.bodyWeight')}
                </label>
                <NumberInput
                  inputMode="decimal"
                  step="0.1"
                  value={meta.body_weight_kg}
                  onChange={(e) => onChange({ body_weight_kg: e.target.value })}
                  className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('workout.meta.energy')}
                </label>
                <NumberInput
                  inputMode="numeric"
                  step="1"
                  min="1"
                  max="10"
                  value={meta.energy_rating}
                  onChange={(e) => onChange({ energy_rating: e.target.value })}
                  className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('workout.meta.startTime')}
                </label>
                <input
                  type="time"
                  value={meta.start_time}
                  onChange={(e) => onChange({ start_time: e.target.value })}
                  className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t('workout.meta.duration')}
                </label>
                <NumberInput
                  inputMode="numeric"
                  step="1"
                  value={meta.duration_min}
                  onChange={(e) => onChange({ duration_min: e.target.value })}
                  className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm"
                  dir="ltr"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                {t('workout.meta.notes')}
              </label>
              <textarea
                value={meta.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
                placeholder={t('workout.meta.notesPlaceholder')}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
                rows={2}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
